import { SubmitButton } from "@/components/ui/button";
import { FormContainer } from "@/components/form/form-container";
import { FormFooter } from "@/components/form/form-footer";
import { H4 } from "@/components/ui/typography";
import { MISSING_ID_IN_ROUTE, STATUS, ROLE } from "@/utils/constant";
import { useId } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { DocumentUploadSection } from "./document-upload-section/DocumentUploadSection";
import { ApprovalSection } from "./approval-section/ApprovalSection";
import { DataCreationSection } from "./data-creation-section/DataCreationSection";
import { StatusFeeder } from "./hptar-section/StatusFeeder";
import {
  ReviewFormSchema,
  reviewFormSchema,
  useDocumentReview,
} from "./utils/useDocumentReview";
import { useSupportingDocumentByRfsId } from "@/api/supporting-document";
import { useRFS } from "@/api/rfs";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import useCheckingProfileValidations from "./utils/useCheckingProfileValidations";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";

export function DocumentReviewForm() {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { handleReview } = useDocumentReview({ id });

  const { data: supportingDocument } = useSupportingDocumentByRfsId({
    rfsId: id,
  });

  const { data: rfs } = useRFS({ id });

  const { fields } = useCheckingProfileValidations({
    rfs,
  });
  const formId = useId();
  const reviewForm = useForm<ReviewFormSchema>({
    resolver: zodResolver(reviewFormSchema),
  });
  if (supportingDocument?.statusFeederAndOlt) {
    reviewForm.setValue(
      "statusFeederAndOlt",
      supportingDocument.statusFeederAndOlt,
    );
  }
  const { roles } = useCurrentUser();
  const isSubmitDisabled = hasCommonElement(roles, [
    ROLE.VENDOR,
    ROLE.MONITORING_TEAM,
    ROLE.VENDOR_VIEW,
  ]);

  return (
    <>
      <FormContainer id={formId} form={reviewForm} onSubmit={handleReview}>
        <H4>Step 1 - Preparation & Validate HPTAR</H4>

        <DataCreationSection />

        <hr />

        <H4>Step 2 - Upload & Validate Supporting Documents</H4>

        <DocumentUploadSection fields={fields} />

        <StatusFeeder />

        {rfs?.statusRfsSubmission !== STATUS.RFS.GENERATING_HPDB && (
          <ApprovalSection />
        )}
      </FormContainer>

      {rfs?.statusRfsSubmission !== STATUS.RFS.GENERATING_HPDB && (
        <FormFooter>
          <SubmitButton form={formId} disabled={isSubmitDisabled} />
        </FormFooter>
      )}
    </>
  );
}
